/**
 * 工具注册配置
 * 定义所有工具的元信息，用于导航、搜索和渲染
 */

// 工具分类定义
export const categories = [
    {
        id: 'data',
        name: '数据工具',
        icon: 'AccountTree',
        description: '数据可视化与结构化数据处理',
    },
    {
        id: 'time',
        name: '时间工具',
        icon: 'Schedule',
        description: '时间戳转换、日期计算等功能',
    },
    {
        id: 'code-text',
        name: '代码文本',
        icon: 'Code',
        description: '文本、编码、代码格式化、JSON 和 SQL 工具',
    },
    {
        id: 'image-qrcode',
        name: '图片与二维码',
        icon: 'Image',
        description: '图片处理、二维码生成与解析',
    },
    {
        id: 'devops',
        name: '运维工具',
        icon: 'Terminal',
        description: 'Linux、网络与部署辅助',
    },
    {
        id: 'calculation',
        name: '通用计算',
        icon: 'Calculate',
        description: '进制转换与常用开发计算',
    },
    {
        id: 'color',
        name: '颜色工具',
        icon: 'Palette',
        description: '颜色选择与格式转换',
    },
];

// 工具列表定义
export const tools = [
    // JSON 工具集
    {
        id: 'json-format',
        name: 'JSON 格式化',
        description: '美化、压缩和校验 JSON 数据',
        category: 'code-text',
        path: '/tools/code-text?group=format&tool=multi&type=json',
        icon: 'DataObject',
        tags: ['json', 'format', 'beautify', 'validate', '格式化', '美化'],
        isNew: true,
        priority: 'P0',
        showInSidebar: false,
        groupId: 'format',
    },
    {
        id: 'json-diff',
        name: 'JSON 对比',
        description: '比较两个 JSON 结构的差异',
        category: 'code-text',
        path: '/tools/code-text?group=text&tool=diff&compare=json',
        icon: 'Compare',
        tags: ['json', 'diff', 'compare', '对比', '差异'],
        priority: 'P0',
        showInSidebar: false,
        groupId: 'text',
    },
    {
        id: 'json-convert',
        name: 'JSON 格式互换',
        description: 'JSON 与 YAML、XML、URL 参数格式实时互转',
        category: 'code-text',
        path: '/tools/code-text?group=json&tool=convert',
        icon: 'SwapHoriz',
        tags: ['json', 'yaml', 'xml', 'url', 'convert', 'transform', '转换', '互转'],
        isNew: true,
        priority: 'P0',
        showInSidebar: false,
        groupId: 'json',
    },
    {
        id: 'json-visualize',
        name: '数据可视化',
        description: '将 JSON、YAML、XML、CSV 数据可视化为交互式图形，支持缩放、拖拽',
        category: 'data',
        path: '/tools/json/visualize',
        icon: 'AccountTree',
        tags: ['json', 'yaml', 'xml', 'csv', 'visualize', 'graph', '可视化', '图形'],
        isNew: true,
        priority: 'P1',
    },
    {
        id: 'json-to-code',
        name: '数据转代码',
        description: '将 JSON、YAML、XML、CSV 数据转换为 TypeScript、Go、Java、Python、Rust 代码',
        category: 'code-text',
        path: '/tools/code-text?group=json&tool=to-code',
        icon: 'Code',
        tags: ['json', 'yaml', 'xml', 'csv', 'typescript', 'go', 'golang', 'java', 'python', 'rust', 'code', '代码', '生成'],
        isNew: true,
        priority: 'P1',
        showInSidebar: false,
        groupId: 'json',
    },

    // 时间工具集
    {
        id: 'timestamp',
        name: '时间戳转换',
        description: 'Unix 时间戳与人类可读时间互转',
        category: 'time',
        path: '/tools/time/timestamp',
        icon: 'Schedule',
        tags: ['timestamp', 'unix', 'date', 'time', '时间戳', '日期'],
        isNew: true,
        priority: 'P0',
    },

    // 编码工具集
    {
        id: 'base64',
        name: 'Base64 编码',
        description: 'Base64 编码与解码转换',
        category: 'code-text',
        path: '/tools/code-text?group=encode&tool=base64',
        icon: 'Code',
        tags: ['base64', 'encode', 'decode', '编码', '解码'],
        showInSidebar: false,
        priority: 'P0',
    },
    {
        id: 'url-encode',
        name: 'URL 编码',
        description: 'URL 参数编码与解码',
        category: 'code-text',
        path: '/tools/code-text?group=encode&tool=url',
        icon: 'Link',
        tags: ['url', 'encode', 'decode', 'percent', '编码', '解码'],
        showInSidebar: false,
        priority: 'P0',
    },
    {
        id: 'jwt',
        name: 'JWT 加解密',
        description: 'JWT Token 解析与生成，支持 HS256/HS384/HS512 算法',
        category: 'code-text',
        path: '/tools/code-text?group=encode&tool=jwt',
        icon: 'Key',
        tags: ['jwt', 'token', 'json', 'web', 'auth', '令牌', '认证', '加密', '解密'],
        showInSidebar: false,
        isNew: true,
        priority: 'P0',
    },

    // 编码转换集 (原编码工具 + 加密哈希 + 标识符生成)
    {
        id: 'hash',
        name: '哈希生成',
        description: 'MD5、SHA-1、SHA-256 等哈希算法',
        category: 'code-text',
        path: '/tools/code-text?group=encode&tool=hash',
        icon: 'Lock',
        tags: ['md5', 'sha', 'hash', '哈希', '加密'],
        showInSidebar: false,
        priority: 'P0',
    },

    // UUID 工具
    {
        id: 'uuid',
        name: 'UUID 生成器',
        description: '生成各版本的 UUID 唯一标识符',
        category: 'code-text',
        path: '/tools/code-text?group=encode&tool=uuid',
        icon: 'Fingerprint',
        tags: ['uuid', 'guid', 'unique', '唯一标识'],
        showInSidebar: false,
        isNew: true,
        priority: 'P0',
    },

    // 图片与二维码工具
    {
        id: 'qrcode-workspace',
        name: '二维码工具',
        description: '生成二维码图片，或从图片中解析二维码内容',
        category: 'image-qrcode',
        path: '/tools/qrcode',
        icon: 'QrCode',
        tags: ['qrcode', 'qr', 'generate', 'decode', '二维码', '生成', '解析'],
        priority: 'P0',
    },
    {
        id: 'qrcode-generate',
        name: '二维码生成',
        description: '根据文本或 URL 生成二维码图片',
        category: 'image-qrcode',
        path: '/tools/qrcode?mode=generate',
        icon: 'QrCode',
        tags: ['qrcode', 'qr', 'generate', '二维码', '生成'],
        priority: 'P0',
        showInSidebar: false,
    },
    {
        id: 'qrcode-decode',
        name: '二维码解析',
        description: '上传或粘贴图片解析二维码内容',
        category: 'image-qrcode',
        path: '/tools/qrcode?mode=decode',
        icon: 'QrCodeScanner',
        tags: ['qrcode', 'qr', 'decode', 'scan', '二维码', '解析', '扫描'],
        isNew: true,
        priority: 'P0',
        showInSidebar: false,
    },

    // === P1 阶段工具 ===

    // 代码文本工作台的四个子入口
    {
        id: 'code-text-text',
        name: '文本工具',
        description: '文本对比、清理、替换和正则处理',
        category: 'code-text',
        path: '/tools/code-text?group=text&tool=diff',
        groupId: 'text',
        icon: 'TextFields',
        tags: ['text', 'diff', 'regex', '文本', '对比'],
        priority: 'P0',
    },
    {
        id: 'code-text-encode',
        name: '编码转换',
        description: 'Base64、URL、JWT、哈希和标识符',
        category: 'code-text',
        path: '/tools/code-text?group=encode&tool=base64',
        groupId: 'encode',
        icon: 'Link',
        tags: ['encode', 'base64', 'url', 'jwt', '编码', '转换'],
        priority: 'P0',
    },
    {
        id: 'code-text-format',
        name: '代码格式化',
        description: 'HTML、CSS、JavaScript、XML、JSON、YAML 和 SQL',
        category: 'code-text',
        path: '/tools/code-text?group=format&tool=multi',
        groupId: 'format',
        icon: 'FormatAlignLeft',
        tags: ['code', 'format', '格式化'],
        priority: 'P1',
    },
    {
        id: 'code-text-sql',
        name: 'SQL 工具',
        description: 'SQL 格式化、对比、方言转换和实体生成',
        category: 'code-text',
        path: '/tools/code-text?group=sql&tool=format',
        groupId: 'sql',
        icon: 'Storage',
        tags: ['sql', 'database', '数据库', '查询'],
        priority: 'P1',
    },
    {
        id: 'code-text-json',
        name: 'JSON 工具',
        description: 'JSON 对比、格式互换和数据转代码',
        category: 'code-text',
        path: '/tools/code-text?group=json&tool=convert',
        groupId: 'json',
        icon: 'DataObject',
        tags: ['json', 'yaml', 'format', 'diff', 'convert', '代码', '数据'],
        priority: 'P1',
    },

    // 代码格式化工具
    {
        id: 'multi-format',
        name: '通用代码格式化',
        description: '支持 HTML, CSS, JS, XML, JSON, YAML, SQL',
        category: 'code-text',
        path: '/tools/code-text?group=format&tool=multi',
        icon: 'FormatAlignLeft',
        tags: ['code', 'format', '格式化'],
        showInSidebar: false,
        isNew: true,
        priority: 'P1',
    },
    {
        id: 'yaml-format',
        name: 'YAML 格式化',
        description: 'YAML 数据美化与校验',
        category: 'code-text',
        path: '/tools/code-text?group=format&tool=multi&type=yaml',
        icon: 'Description',
        tags: ['yaml', 'yml', 'format', '格式化'],
        isNew: true,
        priority: 'P1',
        showInSidebar: false,
        groupId: 'format',
    },

    // SQL 工具
    {
        id: 'sql-format',
        name: 'SQL 格式化',
        description: 'SQL 语句美化与压缩',
        category: 'code-text',
        path: '/tools/code-text?group=sql&tool=format',
        icon: 'Storage',
        tags: ['sql', 'format', 'database', '数据库', '格式化'],
        showInSidebar: false,
        priority: 'P1',
    },
    {
        id: 'sql-to-entity',
        name: 'SQL 转实体类',
        description: 'CREATE TABLE 转 Go/Java/TypeScript/Python 实体类',
        category: 'code-text',
        path: '/tools/code-text?group=sql&tool=entity',
        icon: 'TableChart',
        tags: ['sql', 'entity', 'gorm', 'jpa', 'struct', '实体', '转换', 'go', 'java'],
        showInSidebar: false,
        isNew: true,
        priority: 'P1',
    },
    {
        id: 'sql-diff',
        name: 'SQL 对比',
        description: '比较两个 SQL 脚本的差异，支持格式化对比',
        category: 'code-text',
        path: '/tools/code-text?group=sql&tool=diff',
        icon: 'Compare',
        tags: ['sql', 'diff', 'compare', '对比', '差异', '比较'],
        showInSidebar: false,
        isNew: true,
        priority: 'P1',
    },
    {
        id: 'sql-dialect-convert',
        name: 'SQL 方言转换',
        description: 'PostgreSQL 和 MySQL 的 DDL 语句互转',
        category: 'code-text',
        path: '/tools/code-text?group=sql&tool=convert',
        icon: 'SwapHoriz',
        tags: ['sql', 'convert', 'postgresql', 'mysql', 'ddl', 'pg', '转换', '方言'],
        showInSidebar: false,
        isNew: true,
        priority: 'P1',
    },
    {
        id: 'sql-reference',
        name: 'SQL 速查',
        description: '按场景查看建表、改表、查询、索引、授权和事务命令',
        category: 'code-text',
        path: '/tools/code-text?group=sql&tool=reference',
        icon: 'MenuBook',
        tags: ['sql', 'cheatsheet', 'reference', 'create', 'alter', 'grant', '数据库', '速查', '命令'],
        showInSidebar: false,
        isNew: true,
        priority: 'P1',
    },

    // 文本工具
    {
        id: 'text-diff',
        name: '文本对比',
        description: '比较两段文本的差异，高亮显示变更',
        category: 'code-text',
        path: '/tools/code-text?group=text&tool=diff',
        icon: 'Compare',
        tags: ['text', 'diff', 'compare', '文本', '对比'],
        showInSidebar: false,
        priority: 'P0',
    },

    // 正则工具
    {
        id: 'regex-tester',
        name: '正则表达式测试',
        description: '实时测试正则表达式匹配，高亮显示结果',
        category: 'code-text',
        path: '/tools/code-text?group=text&tool=regex',
        icon: 'FindReplace',
        tags: ['regex', 'regexp', 'pattern', '正则', '匹配'],
        showInSidebar: false,
        isNew: true,
        priority: 'P1',
    },

    // 颜色工具
    {
        id: 'color-picker',
        name: '颜色选择器',
        description: 'HEX、RGB、HSL 颜色格式转换与选择',
        category: 'color',
        path: '/tools/color/picker',
        icon: 'Palette',
        tags: ['color', 'hex', 'rgb', 'hsl', '颜色', '调色板'],
        priority: 'P1',
    },

    // Cron 工具
    {
        id: 'cron-generator',
        name: 'Cron 表达式生成器',
        description: '可视化生成和解析 Cron 定时任务表达式',
        category: 'devops',
        path: '/tools/cron/generator',
        icon: 'Timer',
        tags: ['cron', 'schedule', 'timer', '定时', '调度'],
        priority: 'P1',
    },

    // === P2 阶段工具 ===

    // 图片工具
    {
        id: 'image-base64',
        name: '图片 Base64',
        description: '图片与 Base64 互转，支持预览',
        category: 'image-qrcode',
        path: '/tools/image/base64',
        icon: 'Image',
        tags: ['image', 'base64', 'picture', '图片', '转换'],
        priority: 'P2',
        isNew: true,
    },
    {
        id: 'image-diff',
        name: '图片对比',
        description: '多种模式对比图片：并排、滑动、切换、叠加、像素差异',
        category: 'image-qrcode',
        path: '/tools/image/diff',
        icon: 'Compare',
        tags: ['image', 'diff', 'compare', 'picture', '图片', '对比', '差异'],
        priority: 'P2',
        isNew: true,
    },

    // DevOps 工具
    {
        id: 'chmod-calculator',
        name: 'Chmod 计算器',
        description: 'Linux 文件权限 (rwx) 计算与转换',
        category: 'devops',
        path: '/tools/devops/chmod',
        icon: 'Terminal',
        tags: ['linux', 'chmod', 'permission', 'devops', '权限'],
        priority: 'P2',
    },
    {
        id: 'cidr-calculator',
        name: 'CIDR / 子网计算',
        description: '计算 IPv4 网络地址、广播地址、子网掩码和可用地址范围',
        category: 'devops',
        path: '/tools/devops/cidr',
        icon: 'Lan',
        tags: ['cidr', 'subnet', 'ipv4', 'network', 'ip', '子网', '掩码'],
        priority: 'P1',
        isNew: true,
        showInSidebar: false,
    },
    {
        id: 'certificate-inspector',
        name: 'SSL / TLS 证书解析',
        description: '本地解析 PEM 或 Base64 X.509 证书的主体、有效期和 SAN',
        category: 'devops',
        path: '/tools/devops/certificate',
        icon: 'Security',
        tags: ['ssl', 'tls', 'certificate', 'x509', 'pem', '证书', '安全'],
        priority: 'P1',
        isNew: true,
    },
    {
        id: 'docker-helper',
        name: 'Docker / Compose 辅助',
        description: '生成 docker run 命令和 compose.yaml 配置',
        category: 'devops',
        path: '/tools/devops/docker',
        icon: 'Terminal',
        tags: ['docker', 'compose', 'container', 'command', '容器', '部署'],
        priority: 'P1',
        isNew: true,
    },

    // 数学工具
    {
        id: 'number-base',
        name: '进制转换',
        description: '二进制、八进制、十进制、十六进制转换',
        category: 'calculation',
        path: '/tools/math/base',
        icon: 'Calculate',
        tags: ['math', 'base', 'binary', 'hex', '进制', '计算'],
        priority: 'P2',
    },

    // 网络工具
    {
        id: 'ip-query',
        name: 'IP 查询',
        description: '查询本机公网 IP 及 User-Agent 信息',
        category: 'devops',
        path: '/tools/network/ip',
        icon: 'Public',
        tags: ['ip', 'network', 'address', 'useragent', '网络'],
        priority: 'P2',
        isNew: true,
        showInSidebar: false,
    },
    {
        id: 'dns-lookup',
        name: 'DNS 查询',
        description: '查询域名的 A、AAAA、CNAME、MX、NS 和 TXT 记录',
        category: 'devops',
        path: '/tools/network/dns',
        icon: 'Dns',
        tags: ['dns', 'domain', 'a', 'aaaa', 'mx', 'ns', 'txt', '域名', '解析'],
        priority: 'P1',
        isNew: true,
        showInSidebar: false,
    },

    {
        id: 'network-diagnostics',
        name: '网络诊断',
        description: '子网计算、IP 查询和 DNS 记录诊断',
        category: 'devops',
        path: '/tools/devops/network?tool=cidr',
        icon: 'Lan',
        tags: ['network', 'cidr', 'subnet', 'ip', 'dns', '网络', '诊断', '子网'],
        priority: 'P1',
        isNew: true,
    },
    {
        id: 'devops-command-center',
        name: '运维命令',
        description: '系统诊断、用户和权限常用命令生成',
        category: 'devops',
        path: '/tools/devops/commands',
        icon: 'Terminal',
        tags: ['linux', 'macos', 'system', 'cpu', 'memory', 'disk', 'process', 'user', 'permission', '命令', '诊断'],
        priority: 'P1',
        isNew: true,
    },

    // 代理/CDN 检测
    {
        id: 'proxy-detector',
        name: '代理/CDN 检测',
        description: '检测网站是否使用 CDN 加速或代理服务',
        category: 'devops',
        path: '/tools/network/proxy',
        icon: 'Security',
        tags: ['proxy', 'cdn', 'detect', '代理', 'cdn', '检测'],
        priority: 'P2',
        isNew: true,
    },

    // 文本工具箱 (补充 P2)
    {
        id: 'text-toolkit',
        name: '文本工具箱',
        description: '文本统计、编码转换/查看、大小写转换、清理等',
        category: 'code-text',
        path: '/tools/code-text?group=text&tool=toolkit',
        icon: 'Build',
        tags: ['text', 'toolkit', 'stats', 'case', 'duplicate', '文本', '工具箱'],
        showInSidebar: false,
        priority: 'P2',
        isNew: true,
    },
];

/**
 * 按分类获取工具列表
 * @param {string} categoryId - 分类 ID
 * @returns {Array} 该分类下的工具列表
 */
export const getToolsByCategory = (categoryId) => {
    return tools.filter(tool => tool.category === categoryId && tool.showInSidebar !== false);
};

/**
 * 根据关键词搜索工具
 * @param {string} keyword - 搜索关键词
 * @returns {Array} 匹配的工具列表
 */
export const searchTools = (keyword) => {
    const lowerKeyword = keyword.toLowerCase();
    return tools.filter(tool =>
        tool.name.toLowerCase().includes(lowerKeyword) ||
        tool.description.toLowerCase().includes(lowerKeyword) ||
        tool.tags.some(tag => tag.toLowerCase().includes(lowerKeyword))
    );
};

/**
 * 获取 P0 优先级的工具（首页推荐）
 * @returns {Array} P0 工具列表
 */
export const getFeaturedTools = () => {
    return tools.filter(tool => tool.priority === 'P0' && tool.showInSidebar !== false);
};

/**
 * 获取新工具（带 NEW 标签）
 * @returns {Array} 新工具列表
 */
export const getNewTools = () => {
    return tools.filter(tool => tool.isNew && tool.showInSidebar !== false);
};

export default tools;
